import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap !rounded-[8px] text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ",
  {
    variants: {
      variant: {
        default: "btn-gradient-style !rounded-[8px] text-white hover:brightness-110 focus-visible:ring-0 focus-visible:ring-offset-0 !h-[40px]",
        destructive:
          "bg-destructive !rounded-[8px] text-destructive-foreground hover:brightness-110 focus-visible:ring-0 focus-visible:ring-offset-0 !h-[40px]",
        outline:
          "border border-border !rounded-[8px] bg-background text-foreground hover:bg-accent hover:text-accent-foreground shadow-sm !h-[40px]",
        secondary:
          "bg-secondary !rounded-[8px] text-secondary-foreground hover:bg-secondary/80 !h-[40px]",
        ghost: "hover:bg-accent/50 hover:text-accent-foreground !min-h-[20px]",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80 !h-[40px]",
        premium: "bg-glass border border-border/50 !rounded-[8px] text-foreground hover:bg-accent/20 hover:border-primary/40 backdrop-blur-xl !h-[40px]",
      },
      size: {
        default: "h-12 px-6 py-3",
        sm: "h-10 px-4 text-sm",
        lg: "h-14 px-10 text-base font-semibold",
        icon: "h-12 w-12",
        // Compact square icon buttons for dense toolbars/headers, so callers stop
        // hand-rolling `size="sm" className="h-8 w-8 p-0"`. `icon-sm` (32px) suits
        // ghost buttons; `icon-md` (40px) matches the outline variant's !h-[40px].
        "icon-sm": "h-8 w-8",
        "icon-md": "h-10 w-10",
        xl: "h-16 px-12 text-lg font-semibold",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
